import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import './element-loader'
import tailwindStyles from "../../index.css?inline";

export const RENDER_M_DEFAULT = 'default';

@customElement('e-list')
export class EList extends LitElement {
  @property({ type: Array }) oe: any[] = [];
  @property({ type: String }) renderMode: string = RENDER_M_DEFAULT;
  @property({ type: String }) formType: string = '';
  @property({ type: String }) title: string = '';
  
  // Reference to form component
  @property({ type: Object }) fr: any;

  // New properties for mobile navigation
  @property({ type: Number }) currentStepIndex: number = 0;
  @property({ type: Array }) allowedElements: string[] = [];
  @property({ type: Number }) pos: number = 0;

  constructor() {
    super();
    this.oe = [];
    this.renderMode = RENDER_M_DEFAULT;
  }

  static styles = [
    unsafeCSS(tailwindStyles),
    unsafeCSS(`
      /* Root container - ensure 100vh height */
      :host {
        display: block;
        overflow: hidden;
        position: relative;
      }

      /* Main container - full height with overflow handling */
      .e-list-container {
        height: 100%;
        width: 100%;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        position: relative;
      }

      .container-standard-view-scroll{
        height:85vh;
        overflow-y:auto;
      }

      .e-list-grid-item {
        flex-direction: column;
        height: 100%;
        min-height: 0;
        overflow: hidden;
        flex-shrink: 0;
      }
      
      /* Mobile-first: full width on small screens */
      @media (max-width: 639px) {
        .e-list-grid-item {
          width: 100% !important;
        }
      }
      
      /* Tablet: use specified percentage */
      @media (min-width: 640px) and (max-width: 1023px) {
        .e-list-grid-item.tablet-custom-width {
          width: var(--tablet-width, 100%) !important;
        }
      }
      
      /* Desktop: use specified percentage */
      @media (min-width: 1024px) {
        .e-list-grid-item.desktop-custom-width {
          width: var(--desktop-width, 100%) !important;
        }
      }
      
      /* Standard view scrollable container */
      .standard-view-scroll {
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
        -webkit-overflow-scrolling: touch;
        padding: 16px 0;
      }

      .standard-view-scroll::-webkit-scrollbar {
        width: 6px;
      }

      .standard-view-scroll::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
      }

      .standard-view-scroll::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 3px;
      }

      .standard-view-scroll::-webkit-scrollbar-thumb:hover {
        background: #555;
      }

      /* Mobile canvas container */
      .mobile-canvas-container {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background: #f5f5f5;
        display: flex;
        flex-direction: column;
      }
      
      .mobile-canvas-slider {
        display: flex;
        transition: transform 0.3s ease-in-out;
        height: calc(100% - 70px);
        width: 100%;
        flex: 1;
      }
      
      .mobile-canvas-slide {
        flex: 0 0 100%;
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
        -webkit-overflow-scrolling: touch;
        background: white;
      }
      
      .mobile-content-wrapper {
        height: 100%;
        width: 100%;
        box-sizing: border-box;
        overflow-y: auto;
        overflow-x: hidden;
        -webkit-overflow-scrolling: touch;
      }
      
      /* Hide scrollbar for cleaner look */
      .mobile-canvas-slide::-webkit-scrollbar,
      .mobile-content-wrapper::-webkit-scrollbar {
        display: none;
      }
      
      .mobile-canvas-slide,
      .mobile-content-wrapper {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }

      /* Bottom Navigation Bar */
      .mobile-bottom-nav {
        position: sticky;
        bottom: 0;
        left: 0;
        right: 0;
        height: 70px;
        background: white;
        display: flex;
        justify-content: space-around;
        align-items: center;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        border-top: 1px solid #e5e7eb;
        z-index: 30;
        padding: 0 4px;
        flex-shrink: 0;
      }
      
      .mobile-nav-button {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        padding: 8px 4px;
        cursor: pointer;
        position: relative;
        min-width: 0;
        transition: all 0.2s ease;
        color: #6b7280;
        gap: 4px;
      }
      
      .mobile-nav-button.active {
        color: #3b82f6;
      }
      
      .nav-icon {
        font-size: 24px;
        line-height: 1;
        margin-bottom: 2px;
      }
      
      .nav-label {
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
        padding: 0 2px;
      }
      
      .active-indicator {
        position: absolute;
        top: -2px;
        left: 50%;
        transform: translateX(-50%);
        width: 4px;
        height: 4px;
        border-radius: 2px;
        background: #3b82f6;
      }
      
      /* Touch ripple effect */
      .mobile-nav-button:active {
        background-color: rgba(59, 130, 246, 0.1);
      }

      /* Responsive adjustments */
      @media (max-width: 360px) {
        .nav-label {
          font-size: 10px;
        }
        
        .nav-icon {
          font-size: 20px;
        }
        
        .mobile-bottom-nav {
          height: 60px;
        }
        
        .mobile-canvas-slider {
          height: calc(100% - 60px);
        }
      }

      /* Scrollable grid container for standard view */
      .grid-scroll-container {
        height: 100%;
        overflow-y: auto;
        padding: 8px 0;
        -webkit-overflow-scrolling: touch;
      }

      .grid-scroll-container.none {
        padding:0;
      }

      .grid-scroll-container::-webkit-scrollbar {
        width: 6px;
      }

      .grid-scroll-container::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
      }

      .grid-scroll-container::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 3px;
      }

      .grid-scroll-container::-webkit-scrollbar-thumb:hover {
        background: #555;
      }

      /* Ensure flex wrap works in scroll container */
      .flex-wrap {
        display: flex;
        flex-wrap: wrap;
        width: 100%;
      }

      /* Title styling with margin */
      .e-list-title {
        margin-bottom: 12px;
        font-size: 1.125rem;
        font-weight: 600;
        padding: 0 8px;
        flex-shrink: 0;
      }

      @media (min-width: 640px) {
        .e-list-title {
          font-size: 1.25rem;
          margin-bottom: 16px;
        }
      }

      /* Empty state */
      .empty-state {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        padding: 16px;
        color: #6b7280;
        font-size: 0.875rem;
      }
    `)
  ]

  // Ensure pl property is available for element-loader
  get pl(): any {
    return this;
  }

  eSubmitForm(): any {
    return this.fr?.eSubmitForm?.();
  }

  submitForm(): any {
    return this.fr?.submitForm?.();
  }

  submitForm2(service: any): any {
    return this.fr?.submitForm2?.(service);
  }

  skipForm(service: any, name: string, value: any): any {
    return this.fr?.skipForm?.(service, name, value);
  }

  closeWindow(service: any): any {
    return this.fr?.closeWindow?.(service);
  }

  paramsCopy(): any {
    return this.fr?.paramsCopy?.();
  }

  _dialog(...args: any[]): void {  
    if (this.fr && typeof this.fr._dialog === 'function') {
      this.fr._dialog.apply(this.fr, args);
    } else {
      console.error('Form-render _dialog method not available');
    }
  }

  elementLoaders() {
    if(this.shadowRoot){
    return this.shadowRoot.querySelectorAll('element-loader');
    }
  }
  
  _gridClasses(el: any): string {
    if (!el?.sectionSize) {
      return '24|24|24';
    }
    return el.sectionSize;
  }

  static get is(): string { 
    return 'e-list'; 
  }

  // Check if current template is canvas and mobile view
  private get isCanvasMobile(): boolean {
    return window.TEMPLATE === 'canvas' && window.innerWidth <= 640;
  }

  // Parse section sizes for different breakpoints
  private parseSectionSizes(sectionSize: string): { mobile: number, tablet: number, desktop: number } {
    const sizes = sectionSize.split('|').map(size => parseInt(size, 10));
    return {
      mobile: sizes[2] || sizes[0] || 24,
      tablet: sizes[1] || sizes[0] || 24,
      desktop: sizes[0] || 24
    };
  }

  // Convert 24-col to percentage for each breakpoint
  private getWidthPercentages(sectionSize: string): { mobile: string, tablet: string, desktop: string } {
    const sizes = this.parseSectionSizes(sectionSize);
    
    const convertToPercent = (size: number): string => {
      const percent = (size / 24) * 100;
      return percent === 100 ? '100%' : `${percent}%`;
    };
    
    return {
      mobile: convertToPercent(sizes.mobile),
      tablet: convertToPercent(sizes.tablet),
      desktop: convertToPercent(sizes.desktop)
    };
  }

  // Get CSS custom properties for responsive widths
  private getResponsiveStyleProperties(el: any): string {
    const widths = this.getWidthPercentages(this._gridClasses(el));
    const desktopSize = parseInt(this._gridClasses(el).split('|')[0], 10);
    
    let style = '';
    
    // Only add custom properties if not full width (24)
    if (desktopSize !== 24) {
      style += `--desktop-width: ${widths.desktop}; `;
      style += `--tablet-width: ${widths.tablet}; `;
    }
    
    return style;
  }

  // Get responsive classes based on width
  private getResponsiveClasses(el: any): string {
    const desktopSize = parseInt(this._gridClasses(el).split('|')[0], 10);
    const classes = ['e-list-grid-item'];
    
    // Add responsive width classes only if not full width
    if (desktopSize !== 24) {
      classes.push('tablet-custom-width', 'desktop-custom-width');
    }
    
    return classes.join(' ');
  }

  // Render mobile canvas view with bottom navigation
  private renderMobileCanvas() {
    if (!this.oe || this.oe.length === 0) {
      return html`
        <div class="mobile-canvas-container">
          <div class="empty-state">
            No items to display
          </div>
        </div>
      `;
    }

    return html`
      <div class="mobile-canvas-container">
        <!-- Content Slider -->
        <div class="mobile-canvas-slider" style="transform: translateX(-${this.currentStepIndex * 100}%);">
          ${this.oe.map((element, slideIndex) => html`
            <div class="mobile-canvas-slide" data-index="${slideIndex}">
              <div class="mobile-content-wrapper">
                <element-loader
                  class="block w-full h-full"
                  id="${element._id || element.id}"
                  .element="${element}"
                  .pl="${this}"></element-loader>
              </div>
            </div>
          `)}
        </div>

        <!-- Bottom Navigation -->
        ${this.oe.length > 1 ? html`
          <div class="mobile-bottom-nav">
            ${this.oe.map((element, index) => html`
              <button 
                class="mobile-nav-button ${index === this.currentStepIndex ? 'active' : ''}"
                @click="${() => this.currentStepIndex = index}"
                data-index="${index}"
              >
                ${index === this.currentStepIndex ? html`<span class="active-indicator"></span>` : ''}
                <span class="nav-icon">${index + 1}</span>
                <span class="nav-label">${element.title || element.label || `Step ${index + 1}`}</span>
              </button>
            `)}
          </div>
        ` : ''}
      </div>
    `;
  }

  // Render standard grid view
  private renderStandardView() {
    if (!this.oe || this.oe.length === 0) {
      return html`
        <div class="e-list-container">
          <div class="empty-state">
            No items to display
          </div>
        </div>
      `;
    }

    const containerClasses = window.gateway == "nikobizznet" && window.TEMPLATE == 'canvas' 
      ? 'container mx-auto px-4 md:mx-auto md:max-w-3xl lg:max-w-4xl xl:max-w-5xl' 
      : '';

    return html`
      <div class="e-list-container ${this.formType === "CONTAINER FORM" && window.TEMPLATE == 'canvas' && this.pos > 0 ? 'container-standard-view-scroll' : ''}">
        ${this.formType == "FORM" && window.TEMPLATE !== 'canvas' ? html`
          <h1 class="e-list-title">
            ${this.title}
          </h1>
        ` : ''}

        <div class="grid-scroll-container ${ window.gateway == "Nenachat" ? "none" : ''}">
          <div class="flex-wrap ${containerClasses}">
            ${this.oe.map(el => {
              const responsiveClasses = this.getResponsiveClasses(el);
              const styleProps = this.getResponsiveStyleProperties(el);

              return html`
                <div 
                  class="${responsiveClasses} ${window.TEMPLATE == 'canvas' ? (window.TEMPLATE == 'canvas' && this.pos == 0 ? '' : 'p-2') : (window.gateway == "Nenachat" && window.TEMPLATE == 'service' && el._name == 'nenachat' ? 'p-0' : 'p-2')}"
                  data-id="${el.id}"
                  style="${styleProps} width: 100%;"
                >
                  <element-loader
                    class="block w-full h-full"
                    id="${el.id}"
                    .element="${el}"
                    .pl="${this}"></element-loader>
                </div>
              `;
            })}
          </div>
        </div>
      </div>
    `;
  }

  render() {
    // Use mobile canvas view for canvas template on mobile
    if (this.isCanvasMobile) {
      return this.renderMobileCanvas();
    }
    
    // Use standard view for all other cases
    return this.renderStandardView();
  }

  // Helper method to update allowed elements based on current step
  updateAllowedElementsForStep(stepIndex: number, stepElements: string[]) {
    this.currentStepIndex = stepIndex;
    this.allowedElements = stepElements;
    this.requestUpdate();
  }
}